import { useEffect, useRef, useState } from "react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

export interface MenuItem {
  label: string;
  icon?: LucideIcon;
  onSelect: () => void;
  danger?: boolean;
  separatorAbove?: boolean;
}

export function Menu({
  trigger,
  items,
  side = "bottom",
  align = "end",
  ariaLabel,
  className,
}: {
  trigger: React.ReactNode;
  items: MenuItem[];
  /** Which way the panel opens relative to the trigger. */
  side?: "top" | "bottom";
  align?: "start" | "end";
  ariaLabel?: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <div
        onClick={() => setOpen((value) => !value)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={ariaLabel}
      >
        {trigger}
      </div>

      {open && (
        <div
          role="menu"
          className={cn(
            "absolute z-40 min-w-[200px] animate-scale-in overflow-hidden rounded-xl border border-line bg-paper p-1 shadow-pop",
            side === "top" ? "bottom-full mb-1.5" : "top-full mt-1.5",
            align === "end" ? "right-0" : "left-0",
          )}
        >
          {items.map((item) => (
            <div key={item.label}>
              {item.separatorAbove && (
                <div className="my-1 h-px bg-line" role="separator" />
              )}
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  item.onSelect();
                }}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-[13px] transition",
                  item.danger
                    ? "text-danger hover:bg-dangerSoft"
                    : "text-ink-soft hover:bg-shell hover:text-ink",
                )}
              >
                {item.icon && <item.icon size={15} className="shrink-0" />}
                {item.label}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
